import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import type { ApiResponse, PageableDto, User, UserRequestPayload } from '@shared-models';
import { API_CONFIG, ApiConfig } from '../api-config';
import { mapMutationMessage, mapPageableResponse } from '../../utils/map-pageable';

export interface UserPageResponse {
  items: User[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

@Injectable({ providedIn: 'root' })
export class UserService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private get apiUrl(): string {
    return `${this.apiConfig.apiUrl}/settings/users`;
  }

  private normalizeStatus(status: string | undefined): 'active' | 'inactive' {
    if (!status) return 'active';
    return status.toLowerCase() as 'active' | 'inactive';
  }

  private defaultPhoto(name: string): string {
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'U')}&size=48&background=0D47A1&color=fff`;
  }

  mapToUser(item: Record<string, unknown>): User {
    const firstName = String(item['firstName'] ?? '');
    const jobTitle = item['jobTitle'] ?? item['jobTitles'];
    const profilesRaw = item['profiles'] ?? item['role'];

    return {
      id: String(item['id'] ?? ''),
      photo: String(item['photoUrl'] ?? '') || this.defaultPhoto(firstName),
      code: String(item['code'] ?? ''),
      name: firstName,
      lastNamePaternal: String(item['lastNamePaternal'] ?? ''),
      lastNameMaternal: String(item['lastNameMaternal'] ?? ''),
      identification: String(item['identification'] ?? ''),
      jobTitles: Array.isArray(jobTitle)
        ? jobTitle.map(String).join(', ')
        : String(jobTitle ?? '—'),
      profiles: Array.isArray(profilesRaw)
        ? profilesRaw.map(String).join(', ')
        : String(profilesRaw ?? '—'),
      lastAccess: String(item['lastAccess'] ?? item['lastLogin'] ?? '—'),
      status: this.normalizeStatus(item['status'] as string | undefined),
      email: item['email'] != null ? String(item['email']) : undefined,
      genderId: item['genderId'] != null ? String(item['genderId']) : undefined,
      countryId: item['countryId'] != null ? String(item['countryId']) : undefined,
      identificationId:
        item['identificationId'] != null
          ? String(item['identificationId'])
          : undefined,
      profileId: item['profileId'] != null ? String(item['profileId']) : undefined,
      role: item['role'] != null ? String(item['role']) : undefined,
      jobTitle: item['jobTitle'] != null ? String(item['jobTitle']) : undefined,
      timeClockRegistration: Boolean(item['timeClockRegistration']),
    };
  }

  /**
   * Listado paginado server-side.
   * GET /api/settings/users?page=&size=&sort=&direction=&status=&search=
   */
  getAll(
    page = 0,
    size = 20,
    sort = 'createdAt',
    direction: 'asc' | 'desc' = 'desc',
    search?: string,
    status?: string,
  ): Observable<UserPageResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('sort', sort)
      .set('direction', direction);

    if (search?.trim()) {
      params = params.set('search', search.trim());
    }
    if (status?.trim()) {
      params = params.set('status', status.trim().toUpperCase());
    }

    return this.httpClient
      .get<ApiResponse<PageableDto>>(this.apiUrl, { params })
      .pipe(
        map((response) =>
          mapPageableResponse(response, (item) =>
            this.mapToUser(item as Record<string, unknown>),
          ),
        ),
      );
  }

  getById(id: string): Observable<User> {
    return this.httpClient
      .get<ApiResponse<Record<string, unknown>>>(`${this.apiUrl}/${id}`)
      .pipe(map((response) => this.mapToUser(response.data ?? {})));
  }

  create(data: UserRequestPayload): Observable<{ message: string }> {
    const body = this.toRequestBody(data);
    return this.httpClient
      .post<{ success: boolean; message: string }>(this.apiUrl, body)
      .pipe(map((response) => mapMutationMessage(response, 'Usuario creado exitosamente')));
  }

  update(id: string, data: UserRequestPayload): Observable<{ message: string }> {
    const body = this.toRequestBody(data);
    return this.httpClient
      .put<{ success: boolean; message: string }>(`${this.apiUrl}/${id}`, body)
      .pipe(
        map((response) =>
          mapMutationMessage(response, 'Usuario actualizado exitosamente'),
        ),
      );
  }

  delete(id: string): Observable<{ message: string }> {
    return this.httpClient
      .delete<{ success: boolean; message: string }>(`${this.apiUrl}/${id}`)
      .pipe(
        map((response) => ({
          message: response.message || 'Usuario eliminado exitosamente',
        })),
      );
  }

  updateStatus(id: string): Observable<{ message: string; user?: User }> {
    return this.httpClient
      .patch<{
        success: boolean;
        message: string;
        data?: Record<string, unknown>;
      }>(`${this.apiUrl}/${id}/status`, null)
      .pipe(
        map((response) => ({
          message: response.message || 'Status actualizado exitosamente',
          user: response.data ? this.mapToUser(response.data) : undefined,
        })),
      );
  }

  exportPdf(options?: {
    title?: string;
    status?: string;
    search?: string;
    page?: number;
    size?: number;
  }): Observable<Blob> {
    const params = new HttpParams()
      .set('title', options?.title || 'Reporte de usuarios')
      .set('status', options?.status || '')
      .set('search', options?.search || '')
      .set('page', (options?.page ?? 0).toString())
      .set('size', (options?.size ?? 1000).toString());

    return this.httpClient.get(`${this.apiConfig.apiUrl}/export/pdf/user`, {
      params,
      responseType: 'blob',
    });
  }

  exportExcel(options?: {
    title?: string;
    status?: string;
    search?: string;
    page?: number;
    size?: number;
  }): Observable<Blob> {
    const params = new HttpParams()
      .set('title', options?.title || 'Reporte de usuarios')
      .set('status', options?.status || '')
      .set('search', options?.search || '')
      .set('page', (options?.page ?? 0).toString())
      .set('size', (options?.size ?? 1000).toString());

    return this.httpClient.get(`${this.apiConfig.apiUrl}/export/excel/user`, {
      params,
      responseType: 'blob',
    });
  }

  /** JSON o multipart según presencia de archivo `photo`. */
  private toRequestBody(data: UserRequestPayload): FormData | UserRequestPayload {
    if (data.photo instanceof File) {
      return this.toFormData(data);
    }
    return data;
  }

  private toFormData(data: UserRequestPayload): FormData {
    const formData = new FormData();
    const { photo, ...rest } = data;

    for (const [key, value] of Object.entries(rest)) {
      if (value === null || value === undefined || value === '') continue;
      if (typeof value === 'boolean') {
        formData.append(key, String(value));
      } else {
        formData.append(key, String(value));
      }
    }

    if (photo) {
      formData.append('photo', photo);
    }

    return formData;
  }
}
